import { VehicleSurveyImage } from "../interfaces/altair";
import { apiFetch } from "../utils/api";

// ═══════════════════════════════════════════════════════════
// PER-SIDE VALIDATION (live survey flow)
// ═══════════════════════════════════════════════════════════

export interface ValidateVehicleImageResponse {
  isValid: boolean;
  confidenceScore: number;
  damageDescription?: string | null;
  chassisNumber?: string | null;
  /** "match" | "mismatch" | "skipped" — only populated for Chassis side */
  makeModelMatchResult?: string;
  message?: string | null;
}

/**
 * Validates a single vehicle side image immediately after capture.
 * Used by the live survey flow (voice and button paths).
 * Throws on network/server error — never fails silently.
 */
export async function validateVehicleImage(
  imageData: string,
  side: string,
  vehicleMake?: string,
  vehicleModel?: string
): Promise<ValidateVehicleImageResponse> {
  const normalizedData = imageData.startsWith("data:")
    ? imageData
    : `data:image/jpeg;base64,${imageData}`;

  const response = await apiFetch("/Gemini/validate-vehicle-image", {
    method: "POST",
    body: JSON.stringify({
      Data: normalizedData,
      DocumentName: `${side}.jpeg`,
      Side: side,
      ...(vehicleMake ? { VehicleMake: vehicleMake } : {}),
      ...(vehicleModel ? { VehicleModel: vehicleModel } : {}),
    }),
  });

  if (!response.ok) {
    let errorMessage = `Backend error validating vehicle image: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return {
    isValid: data.isValid ?? data.IsValid ?? false,
    confidenceScore: data.confidenceScore ?? data.ConfidenceScore ?? 0,
    damageDescription: data.damageDescription ?? data.DamageDescription ?? null,
    chassisNumber: data.chassisNumber ?? data.ChassisNumber ?? null,
    makeModelMatchResult: data.makeModelMatchResult ?? data.MakeModelMatchResult,
    message: data.message ?? data.Message ?? null,
  };
}

// ═══════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════

export interface AnalyzeVehicleImagesResponse {
  vehicleAnalyzeDescription: string;
  /** Chassis number extracted from the inspection images, null if not found. */
  chassisNumber?: string | null;
  /**
   * Result of comparing chassis-decoded make/model against user-stated make/model.
   * Values: "match" | "mismatch" | "skipped"
   */
  makeModelMatchResult?: string;
}

// ═══════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════

/**
 * Analyzes vehicle images using Gemini AI to generate damage description, extract chassis number,
 * and optionally verify make/model against user-stated values.
 * @param images Array of vehicle survey images with base64 data
 * @param vehicleMake Make of the vehicle as stated by the user (optional, policy flow only)
 * @param vehicleModel Model of the vehicle as stated by the user (optional, policy flow only)
 * @returns Promise with damage description, chassis number, and make/model match result
 */
export const analyzeVehicleImages = async (
  images: VehicleSurveyImage[],
  vehicleMake?: string,
  vehicleModel?: string
): Promise<AnalyzeVehicleImagesResponse> => {
  const response = await apiFetch("/Gemini/Analyze-vehicle-Images", {
    method: "POST",
    body: JSON.stringify({
      Documents: images.map((img) => ({
        DocumentName: `${img.side}.jpeg`,
        Data: img.data.startsWith("data:")
          ? img.data
          : `data:image/jpeg;base64,${img.data}`,
      })),
      ...(vehicleMake ? { VehicleMake: vehicleMake } : {}),
      ...(vehicleModel ? { VehicleModel: vehicleModel } : {}),
    }),
  });

  if (!response.ok) {
    let errorMessage = `Backend error while analyzing vehicle images: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      // Use default error message if JSON parsing fails
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return {
    vehicleAnalyzeDescription:
      data.vehicleAnalyzeDescription ||
      data.VehicleAnalyzeDescription ||
      "السيارة بحالة جيده",
    chassisNumber: data.chassisNumber ?? data.ChassisNumber ?? null,
    makeModelMatchResult:
      data.makeModelMatchResult ?? data.MakeModelMatchResult ?? "skipped",
  };
};

